import {
  SHCheckbox,
  SHFormControlLabel,
  SHLabel,
  SHStack,
} from "@components/design-systems";
import { Controller, useFormContext } from "react-hook-form";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import {
  AnalysisSetupDTO,
  AnalysisSetupStepRenderedFieldProps,
} from "@models/platform-analysis/entities/steps/setup";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { AnalysisFeatureSelectionsConfig } from "@pages/platform-analysis-v2/_id/steps/setup/configs";
import { useUserPermissions } from "@hooks/userUserPermission";
import { useEffect } from "react";
import { useIsNew } from "@hooks/useIsNew";
import { FormHelperText } from "@mui/material";

export const AnalysisSetupStepSelectionField = ({
  userType,
  disabled,
}: AnalysisSetupStepRenderedFieldProps) => {
  const isNew = useIsNew();
  const { isAdviserGroup } = useUserPermissions();
  const {
    setStepHidden,
    setAnalysisDiscardedStages,
    platformAnalysis: storedPlatformAnalysis,
  } = usePlatformAnalysis();

  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<AnalysisSetupDTO>();

  const updateHasSteps = ({
    stepKey,
    isChecked,
    triggerValidation = true,
  }: {
    stepKey: AnalysisStep;
    isChecked: boolean;
    triggerValidation?: boolean;
  }) => {
    let currentSteps = getValues("hasSteps") ?? [];
    if (isChecked) {
      currentSteps.push(stepKey);
    } else {
      currentSteps = currentSteps.filter((key) => key !== stepKey);
    }
    setValue("hasSteps", currentSteps, {
      shouldDirty: triggerValidation,
      shouldValidate: triggerValidation,
    });
  };

  const updateDiscardedStages = () => {
    if (isNew) return;
    const originalSteps = storedPlatformAnalysis?.setup?.hasSteps || [];
    const newSteps = getValues("hasSteps") || [];
    const discardedSteps = originalSteps.filter(
      (step) => !newSteps.includes(step),
    );
    setAnalysisDiscardedStages(discardedSteps);
  };

  const handleOnChangeSelectSteps = ({
    stepKey,
    isChecked,
    triggerValidation = true,
  }: {
    stepKey: AnalysisStep;
    isChecked: boolean;
    triggerValidation?: boolean;
  }) => {
    setStepHidden({ step: stepKey, isHidden: !isChecked });
    updateHasSteps({ stepKey, isChecked, triggerValidation });
    updateDiscardedStages();
  };

  useEffect(() => {
    if (!isNew) return;
    AnalysisFeatureSelectionsConfig.forEach((item) => {
      const { key, defaultValue } = item;
      setValue(key, defaultValue[userType]);
      handleOnChangeSelectSteps({
        stepKey: item.step,
        isChecked: defaultValue[userType],
        triggerValidation: false,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdviserGroup, isNew]);

  return (
    <SHStack direction={"column"}>
      <SHLabel
        label={`What should your platform ${
          userType === "supplier" ? "analysis" : "review"
        } include?`}
        disabled={disabled}
        required
      />

      {AnalysisFeatureSelectionsConfig.map((feature) => {
        const { step, key, titles, allowModify } = feature;
        const notAllowModify = !allowModify[userType];

        return (
          <Controller
            key={key}
            name={key}
            control={control}
            render={({ field: { onChange, value: fieldValue } }) => (
              <SHFormControlLabel
                disabled={disabled || notAllowModify}
                value={step}
                control={<SHCheckbox />}
                label={titles[userType]}
                checked={!!fieldValue}
                onChange={() => {
                  const newValue = !fieldValue;
                  onChange(newValue);
                  handleOnChangeSelectSteps({
                    stepKey: step,
                    isChecked: newValue,
                  });
                }}
              />
            )}
          />
        );
      })}

      <FormHelperText error>{errors?.hasSteps?.message}</FormHelperText>
    </SHStack>
  );
};
